import { computed, inject, Injectable, Signal } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, filter, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { BeneficiaryDb, injectDb } from './db';
import { AuthService } from './auth.service';
import {
  BeneficiaryModel,
  HydratedSubscriptionEntry,
  SubscriptionEntries,
} from '@models/beneficiaries/Beneficiary.model';
import { BeneficiaryStorage, injectStorage } from './storage';
import { BeneficiarySubscriptionModel } from '@models/beneficiaries/BeneficiarySubscription.model';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { getProductSubscriptionEntry } from '@assets/utils/beneficiaries/beneficiarySubscription.util';
import { BeneficiaryApi, injectApi } from './api';
import { OpenProductName } from '@models/products/Products.model';

/** Order is the selection priority: the first supported product with a subscription wins. */
export const SUPPORTED_BENEFICIARY_PRODUCTS: readonly OpenProductName[] = ['OPEN2PAY', 'PRUNEO'];

@Injectable({
  providedIn: 'root',
})
export class UserService {
  readonly #db: BeneficiaryDb = injectDb();
  #authService: AuthService = inject(AuthService);
  readonly #storage: BeneficiaryStorage = injectStorage();
  readonly #api: BeneficiaryApi = injectApi();

  #email$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  #isActivated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  #refreshBeneficiary: BehaviorSubject<void> = new BehaviorSubject<void>(void 0);

  /** A `shareReplay(1)` that actually errors latches forever and never resubscribes: the failure is carried in-band on purpose. */
  #beneficiaryResult$: Observable<{ beneficiary?: BeneficiaryModel, error?: unknown }> = combineLatest([
    this.#authService.user$,
    this.#refreshBeneficiary,
  ]).pipe(
    switchMap(([authUser]) => authUser
      ? this.#db.getDoc$<BeneficiaryModel>('BENEFICIARIES', authUser.uid).pipe(
        map((beneficiary: BeneficiaryModel | undefined) => ({ beneficiary })),
        catchError((error: unknown) => of({ error })),
      )
      : of({ beneficiary: undefined }),
    ),
    shareReplay(1),
  );

  /** Propagates the read failure, which `authGuard` needs to tell "unreachable" from "no beneficiary". */
  beneficiary$: Observable<BeneficiaryModel | undefined> = this.#beneficiaryResult$.pipe(
    switchMap(result => 'error' in result && result.error
      ? throwError(() => result.error)
      : of(result.beneficiary)),
  );

  /** `toSignal` re-throws on read from an erroring observable, so every signal here derives from this flattened stream instead of the raw one. */
  beneficiaryOrUndefined$: Observable<BeneficiaryModel | undefined> = this.#beneficiaryResult$.pipe(
    map(result => result.beneficiary),
  );

  beneficiary: Signal<BeneficiaryModel | undefined> = toSignal(this.beneficiaryOrUndefined$.pipe(filter(Boolean)));

  activeSubscriptionEntry: Signal<HydratedSubscriptionEntry | undefined> = computed(() => this.getActiveSubscriptionEntry(this.beneficiary()?.subscriptions));
  subscriptionId: Signal<string | undefined> = computed(() => this.activeSubscriptionEntry()?.subscriptionId);

  subscription: Signal<BeneficiarySubscriptionModel | undefined> = toSignal(this.beneficiaryOrUndefined$.pipe(
    filter(Boolean),
    map((beneficiary: BeneficiaryModel) => ({ beneficiary, subscriptionEntry: this.getActiveSubscriptionEntry(beneficiary.subscriptions) })),
    filter((context): context is { beneficiary: BeneficiaryModel; subscriptionEntry: HydratedSubscriptionEntry } =>
      context.subscriptionEntry !== undefined),
    switchMap(({ beneficiary, subscriptionEntry }) =>
      this.#api.call$('BENEFICIARIES-getBeneficiarySubscription_onCall', {
        beneficiaryId: beneficiary.uid,
        subscriptionId: subscriptionEntry.subscriptionId,
      })),
  ));

  agencyLogoUrl$: Observable<string | undefined> = toObservable(this.subscription).pipe(
    filter(Boolean),
    switchMap((subscription) => this.#storage.fileUrl$(`agency_logos/${subscription.agencyId}`)),
    catchError(() => of(undefined)),
    shareReplay(1),
  );

  agencyLogoUrl: Signal<string | undefined> = toSignal(this.agencyLogoUrl$);

  isBeneficiaryActive: Signal<boolean> = computed(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const dateToday = now.getDate();

    const startOfDay: Date = new Date(year, month, dateToday);
    const endOfDay: Date = new Date(year, month, dateToday + 1);

    const subscriptionEntry: HydratedSubscriptionEntry | undefined = this.activeSubscriptionEntry();
    return !!subscriptionEntry
      && (subscriptionEntry.firstRightDate).toMillis() <= startOfDay.valueOf()
      && ((subscriptionEntry.deactivationDate
          ? (subscriptionEntry.deactivationDate).toMillis() >= endOfDay.valueOf()
          : true)
      );
  });

  setEmail$(email: string): void {
    this.#email$.next(email);
  }

  getEmail$(): Observable<string | null> {
    return this.#email$.asObservable();
  }

  setUserIsActivated$(isActivated: boolean): void {
    this.#isActivated$.next(isActivated);
  }

  refreshBeneficiary(): void {
    this.#refreshBeneficiary.next();
  }

  public getActiveSubscriptionEntry(subscriptions: SubscriptionEntries | undefined): HydratedSubscriptionEntry | undefined {
    for (const product of SUPPORTED_BENEFICIARY_PRODUCTS) {
      const entry: HydratedSubscriptionEntry | undefined = getProductSubscriptionEntry(subscriptions, product);
      if (entry) {
        return entry;
      }
    }
    return undefined;
  }

  /** @deprecated use `getActiveSubscriptionEntry` (or the `activeSubscriptionEntry` signal) instead. */
  public getOpen2PaySubscriptionEntry(subscriptions: SubscriptionEntries | undefined): HydratedSubscriptionEntry<'OPEN2PAY'> | undefined {
    return getProductSubscriptionEntry(subscriptions, 'OPEN2PAY');
  }
}
